import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";
import { buildPageStyles, ensureProposalFontsLoaded } from "@/lib/proposalThemes";

export default function ProposalPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [sp] = useSearchParams();
  const printMode = sp.get("print") === "1";

  useEffect(() => { ensureProposalFontsLoaded(); }, []);

  useEffect(() => {
    api.get(`/proposals/by-slug/${slug}`).then(setP).catch(() => setP({ _error: true }));
  }, [slug]);

  const [customTheme, setCustomTheme] = useState(null);
  useEffect(() => {
    if (p && p.custom_theme_id) {
      api.get(`/proposal-themes-public/${p.custom_theme_id}`).then(setCustomTheme).catch(() => setCustomTheme(null));
    } else {
      setCustomTheme(null);
    }
  }, [p?.custom_theme_id]); // eslint-disable-line

  useEffect(() => {
    if (printMode && p && !p._error) {
      document.title = `Propuesta · ${p.title}`;
      setTimeout(() => window.print(), 600);
    }
  }, [printMode, p]);

  if (!p) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Cargando...</div>;
  if (p._error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Propuesta no encontrada o no publicada.</div>;

  const accent = p.theme_accent || "#FF0055";
  const { vars, isLight } = buildPageStyles({
    preset: p.bg_preset || "pink-glow",
    mode: p.theme_mode || "dark",
    accent,
    gFrom: p.gradient_from,
    gTo: p.gradient_to,
    bgColor: p.bg_color,
  });
  const fontHead = p.font_heading || "Unbounded";
  const fontBody = p.font_body || "Manrope";
  const cover = resolveMediaUrl(p.cover_image);
  const logo = resolveMediaUrl(p.logo_image);

  // Sizing & motion
  const animLevel = p.animation_intensity || "subtle";
  const showGlow = p.layer_glow !== false;
  const titleSizeCls = ({
    small: "text-4xl md:text-5xl lg:text-6xl",
    medium: "text-5xl md:text-7xl lg:text-8xl",
    large: "text-6xl md:text-8xl lg:text-[140px]",
    xl: "text-7xl md:text-9xl lg:text-[180px]",
  })[p.cover_size || "large"];
  const logoSizeStyle = ({
    small: "max-w-[110px] max-h-[40px]",
    medium: "max-w-[160px] max-h-[60px]",
    large: "max-w-[240px] max-h-[80px]",
  })[p.logo_size || "medium"];
  const programLogoSize = ({
    small: "w-12 h-12",
    medium: "w-16 h-16",
    large: "w-24 h-24",
  })[p.program_logo_size || "medium"];
  const programLogoBig = ({
    small: "w-16 h-16",
    medium: "w-24 h-24",
    large: "w-32 h-32",
  })[p.program_logo_size || "medium"];
  const hostPhotoSize = ({
    small: "w-20 h-20 md:w-24 md:h-24",
    medium: "w-28 h-28 md:w-36 md:h-36",
    large: "w-36 h-36 md:w-48 md:h-48",
  })[p.host_photo_size || "medium"];

  const motionPropsCover = animLevel === "none"
    ? { initial: false }
    : animLevel === "dynamic"
      ? { initial: { opacity: 0, y: 60, scale: 0.95 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } }
      : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8 } };
  const motionPropsCard = (i = 0) => animLevel === "none"
    ? { initial: false }
    : animLevel === "dynamic"
      ? { initial: { opacity: 0, y: 40, rotateX: -8 }, whileInView: { opacity: 1, y: 0, rotateX: 0 }, viewport: { once: true }, transition: { delay: i * 0.07, duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
      : { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { delay: i * 0.05 } };

  return (
    <div className="proposal-root min-h-screen" style={{ ...vars, color: "var(--p-fg)", background: "var(--p-bg)", fontFamily: `'${fontBody}', system-ui, sans-serif` }} data-testid="proposal-page">
      <style>{`
        @media print {
          body { background: var(--p-bg) !important; }
          .no-print { display: none !important; }
          section.proposal-page { page-break-after: always; min-height: 100vh; }
        }
        body:has(.proposal-root) { background: var(--p-bg, #050505); }
        .proposal-root .p-heading { font-family: '${fontHead}', system-ui, sans-serif; letter-spacing: -0.02em; }
        .proposal-root .p-accent { color: var(--p-accent); }
        .proposal-root .p-bg-accent { background-color: var(--p-accent); }
        .proposal-root .p-soft { color: var(--p-fg-soft); }
        .proposal-root .p-muted { color: var(--p-fg-muted); }
        .proposal-root .p-grad-text {
          background: linear-gradient(120deg, var(--p-accent) 0%, var(--p-grad-to) 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .proposal-root .p-card { background: var(--p-card); border: 1px solid var(--p-border); backdrop-filter: blur(10px); }
        .proposal-root .p-divider { background: var(--p-border); }
      `}</style>

      {!printMode && (
        <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--p-accent)] text-white text-sm font-semibold rounded-sm shadow-lg">
            <Download className="w-4 h-4" /> Imprimir / PDF
          </button>
        </div>
      )}

      {/* Cover */}
      <PageSection sectionStyle={cover && (p.bg_preset === "cover" || !p.bg_preset)
        ? { backgroundImage: `${isLight ? "linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.85))" : "linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.9))"}, url(${cover})`, backgroundSize: "cover", backgroundPosition: "center" }
        : undefined} preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color} center customTheme={customTheme} pageIdx={0} customMode={p.custom_theme_mode}>
        {logo && <img src={logo} alt="logo" className={`absolute top-8 left-8 ${logoSizeStyle} object-contain z-10`} />}
        {showGlow && <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full blur-[140px] opacity-30" style={{ background: accent }} />}
        {showGlow && <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[140px] opacity-25" style={{ background: "var(--p-grad-to)" }} />}
        <motion.div {...motionPropsCover} className="relative text-center px-6">
          <p className="text-xs uppercase tracking-[0.4em] p-accent mb-8 font-bold">INFLUCAST · Propuesta {p.year}</p>
          <h1 className={`p-heading p-grad-text ${titleSizeCls} font-black leading-[0.9] mb-6`}>{p.title}</h1>
          {p.client_name && <p className="text-xl md:text-2xl p-soft">Para <strong style={{ color: "var(--p-fg)" }}>{p.client_name}</strong></p>}
        </motion.div>
      </PageSection>

      {/* Intro */}
      {(p.intro_title || p.intro_text) && (
        <PageSection preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color}>
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.4em] p-accent mb-6 font-bold">Introducción</p>
            <h2 className="p-heading text-4xl md:text-6xl font-black mb-8">{p.intro_title}</h2>
            <p className="text-base md:text-lg leading-relaxed p-soft whitespace-pre-wrap">{p.intro_text}</p>
          </div>
        </PageSection>
      )}

      {/* Programs */}
      {p.programs?.length > 0 && (
        <>
          <PageSection preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color}>
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
              <p className="text-xs uppercase tracking-[0.4em] p-accent mb-6 font-bold">Programación {p.year}</p>
              <h2 className="p-heading text-4xl md:text-6xl font-black mb-12">Nuestros programas</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {p.programs.map((pr, i) => (
                  <motion.div key={i} {...motionPropsCard(i)} className="p-card rounded-sm p-5 flex items-center gap-4">
                    {pr.logo && <img src={resolveMediaUrl(pr.logo)} alt={pr.name} className={`${programLogoSize} object-contain`} />}
                    <div className="flex-1">
                      <h3 className="p-heading font-black text-xl">{pr.name}</h3>
                      <p className="text-xs uppercase tracking-widest p-accent mt-1 font-bold">{pr.schedule}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </PageSection>

          {p.programs.map((pr, i) => (
            <PageSection key={`prog-${i}`} preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color}>
              <div className="max-w-6xl mx-auto px-6 lg:px-10 w-full">
                <div className="flex items-center gap-6 mb-10">
                  {pr.logo && <img src={resolveMediaUrl(pr.logo)} alt={pr.name} className={`${programLogoBig} object-contain`} />}
                  <div>
                    <h2 className="p-heading text-4xl md:text-6xl font-black">{pr.name}</h2>
                    {pr.schedule && <p className="text-sm uppercase tracking-widest p-accent mt-2 font-bold">{pr.schedule}</p>}
                  </div>
                </div>
                {pr.description && <p className="text-base md:text-lg leading-relaxed p-soft max-w-3xl mb-12 whitespace-pre-wrap">{pr.description}</p>}
                {pr.hosts?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest p-muted mb-4 font-bold">Conductores</p>
                    <div className="flex flex-wrap gap-5">
                      {pr.hosts.map((h, hi) => (
                        <div key={hi} className="text-center">
                          <div className={`${hostPhotoSize} rounded-full overflow-hidden p-card mb-3`}>
                            {h.photo && <img src={resolveMediaUrl(h.photo)} alt={h.name} className="w-full h-full object-cover" />}
                          </div>
                          <p className="font-semibold text-sm">{h.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </PageSection>
          ))}
        </>
      )}

      {/* Stats */}
      {p.stats?.length > 0 && (
        <PageSection preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color}>
          <div className="max-w-6xl mx-auto px-6 lg:px-10 w-full">
            <p className="text-xs uppercase tracking-[0.4em] p-accent mb-6 font-bold">Data general</p>
            <h2 className="p-heading text-4xl md:text-6xl font-black mb-12">Audiencia & alcance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px p-divider rounded-sm overflow-hidden">
              {p.stats.map((s, i) => (
                <div key={i} className="p-6 md:p-8" style={{ background: "var(--p-bg)" }}>
                  <p className="text-xs uppercase tracking-widest p-accent mb-6 font-bold">{s.platform}</p>
                  <dl className="space-y-4">
                    <div><dt className="text-[10px] uppercase tracking-widest p-muted">Followers</dt><dd className="p-heading text-3xl font-black">{s.followers || "—"}</dd></div>
                    <div><dt className="text-[10px] uppercase tracking-widest p-muted">Views</dt><dd className="p-heading text-2xl font-black">{s.views || "—"}</dd></div>
                    <div><dt className="text-[10px] uppercase tracking-widest p-muted">Interacciones</dt><dd className="p-heading text-2xl font-black">{s.interactions || "—"}</dd></div>
                  </dl>
                </div>
              ))}
            </div>
          </div>
        </PageSection>
      )}

      {/* Packages */}
      {p.packages?.length > 0 && (
        <>
          <PageSection preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color} center>
            <div className="max-w-5xl mx-auto px-6 text-center">
              <p className="text-xs uppercase tracking-[0.4em] p-accent mb-6 font-bold">Propuesta comercial</p>
              <h2 className="p-heading p-grad-text text-5xl md:text-8xl lg:text-[160px] font-black">Paquetes</h2>
            </div>
          </PageSection>
          {p.packages.map((pk, i) => (
            <PageSection key={`pkg-${i}`} preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color}>
              <div className="max-w-4xl mx-auto px-6 lg:px-10 w-full">
                <motion.div {...motionPropsCard(i)} className={`p-card rounded-sm p-8 lg:p-12 ${pk.featured ? "ring-2" : ""}`} style={pk.featured ? { boxShadow: `0 0 80px -10px ${accent}`, borderColor: accent } : undefined}>
                  <p className="text-xs uppercase tracking-widest p-accent mb-4 font-bold">Plan {String(i + 1).padStart(2, "0")}</p>
                  <h3 className="p-heading text-4xl md:text-6xl font-black mb-2">{pk.name}</h3>
                  {pk.exclusivity && <p className="text-sm p-accent font-bold mb-4">Exclusividad en tu categoría</p>}
                  {pk.price && (
                    <div className="mb-10">
                      <span className="p-heading text-5xl md:text-6xl font-black">{pk.price}</span>
                      <span className="text-lg p-soft ml-3">{pk.currency || "BOB"} {pk.frequency}</span>
                    </div>
                  )}
                  <ul className="space-y-3">
                    {(pk.features || []).map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3 text-base">
                        <span className="w-1.5 h-1.5 rounded-full p-bg-accent mt-2.5 flex-shrink-0" />
                        <span className="p-soft" style={{ color: "var(--p-fg)" }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </PageSection>
          ))}
        </>
      )}

      {/* Closing */}
      <PageSection preset={p.bg_preset} mode={p.theme_mode} gFrom={p.gradient_from} gTo={p.gradient_to} accent={accent} bgColor={p.bg_color} center customTheme={customTheme} pageIdx={(customTheme?.pages?.length || 1) - 1} customMode={p.custom_theme_mode}>
        {logo && <img src={logo} alt="logo" className={`absolute top-8 left-1/2 -translate-x-1/2 ${logoSizeStyle} object-contain z-10`} />}
        {showGlow && <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full blur-[140px] opacity-40" style={{ background: accent }} />}
        <div className="relative text-center px-6">
          <h2 className="p-heading p-grad-text text-6xl md:text-9xl lg:text-[180px] font-black mb-8">{p.closing_text}</h2>
          <p className="text-xl p-soft">{p.year} · INFLUCAST</p>
        </div>
      </PageSection>
    </div>
  );
}

function PageSection({ children, preset, mode, accent, gFrom, gTo, bgColor, center, sectionStyle, customTheme, pageIdx, customMode }) {
  const { bgCss } = buildPageStyles({ preset, mode, accent, gFrom, gTo, bgColor });
  const styleObj = sectionStyle || cssTextToObject(bgCss);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const themePage = customTheme?.pages?.[Math.min(pageIdx ?? 0, (customTheme?.pages?.length || 1) - 1)];
  const themeBgUrl = themePage ? `${BACKEND}/api/media/${themePage.media_id}` : null;
  const overlay = customMode === "overlay";
  return (
    <section
      className={`proposal-page min-h-screen relative overflow-hidden py-24 ${center ? "flex items-center justify-center" : "flex items-center"}`}
      style={styleObj}
    >
      {themeBgUrl && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${themeBgUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: overlay ? 0.35 : 1,
            mixBlendMode: overlay ? "screen" : "normal",
            zIndex: 0,
          }}
        />
      )}
      <div className={themeBgUrl ? "relative w-full" : "contents"} style={themeBgUrl ? { zIndex: 1 } : undefined}>{children}</div>
    </section>
  );
}

function cssTextToObject(cssText) {
  const obj = {};
  cssText.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const k = decl.slice(0, idx).trim();
    const v = decl.slice(idx + 1).trim();
    if (!k || !v) return;
    const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = v;
  });
  return obj;
}
